import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../utils/trpc'

interface FieldConfig {
	id?: string
	name: string
	sources: Array<{ type: 'customFields' | 'hierarchy' | 'user' | 'system' | 'evaluator'; key: string }>
	connector?: string
	order: number
	formId?: string | null
	persona?: string | null
	visibleToRoles?: string[] | null
}

interface AvailableFields {
	customFields: Array<{ key: string; type: 'string' | 'number' }>
	hierarchyFields: Array<{ key: string; label: string }>
	userFields: Array<{ key: string; label: string }>
	systemFields: Array<{ key: string; label: string }>
	evaluatorFields: Array<{ key: string; label: string }>
}

interface EvaluationHeaderState {
	// Data
	fields: FieldConfig[]
	availableFields: AvailableFields | null
	organizationId: string | null
	formId: string | null
	selectedPersona: string | null

	// UI State
	isLoading: boolean
	isSaving: boolean
	error: string | null

	// Actions
	initialize: (organizationId: string, formId?: string | null, persona?: string | null) => Promise<void>
	fetchConfig: () => Promise<void>
	fetchAvailableFields: () => Promise<void>
	saveConfig: () => Promise<void>
	copyFromOrgDefaults: () => Promise<void>
	reset: () => void

	// Field Operations
	addField: (field: Omit<FieldConfig, 'id' | 'order'>) => void
	removeField: (id: string) => void
	updateFieldName: (id: string, name: string) => void
	moveFieldUp: (index: number) => void
	moveFieldDown: (index: number) => void
	setFields: (fields: FieldConfig[]) => void
	setSelectedPersona: (persona: string | null) => Promise<void>
}

export const useEvaluationHeaderStore = create<EvaluationHeaderState>()(
	devtools(
		(set, get) => ({
			// Initial State
			fields: [],
			availableFields: null,
			organizationId: null,
			formId: null,
			selectedPersona: null,
			isLoading: false,
			isSaving: false,
			error: null,

			// Initialize the store with org/form context
			initialize: async (organizationId: string, formId?: string | null, persona?: string | null) => {
				set({
					organizationId,
					formId: formId ?? null,
					selectedPersona: persona ?? null,
					isLoading: true,
					error: null,
				})
				try {
					await Promise.all([get().fetchConfig(), get().fetchAvailableFields()])
				} catch (error) {
					set({ error: error instanceof Error ? error.message : 'Failed to initialize' })
				} finally {
					set({ isLoading: false })
				}
			},

			// Fetch evaluation header config
			fetchConfig: async () => {
				const { organizationId, formId, selectedPersona } = get()
				if (!organizationId) return

				try {
					const config = await trpcClient.admin.userDashboard.getEvaluationHeaderConfig.query({
						organizationId,
						formId,
						persona: selectedPersona,
						skipFallback: !!formId,
					})

					const fieldsWithIds = (config || []).map((field, idx) => ({
						...field,
						id: field.id || `existing-${idx}-${Date.now()}`,
					}))

					set({ fields: fieldsWithIds.sort((a, b) => a.order - b.order) })
				} catch (error) {
					set({ error: error instanceof Error ? error.message : 'Failed to fetch config' })
					throw error
				}
			},

			// Fetch available fields (custom fields, hierarchy fields, user fields)
			fetchAvailableFields: async () => {
				const { organizationId } = get()
				if (!organizationId) return

				try {
					const availableFields = await trpcClient.admin.userDashboard.getAvailableFields.query({
						organizationId,
					})
					set({ availableFields })
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch available fields',
					})
					throw error
				}
			},

			// Save the current configuration
			saveConfig: async () => {
				const { organizationId, formId, fields, selectedPersona } = get()
				if (!organizationId) return

				set({ isSaving: true, error: null })
				try {
					await trpcClient.admin.userDashboard.updateEvaluationHeaderConfig.mutate({
						organizationId,
						formId,
						persona: selectedPersona,
						config: fields.map((f, index) => ({ ...f, order: index })),
					})
				} catch (error) {
					set({ error: error instanceof Error ? error.message : 'Failed to save config' })
					throw error
				} finally {
					set({ isSaving: false })
				}
			},

			// Copy org-level config as starting point for form-specific config
			copyFromOrgDefaults: async () => {
				const { organizationId, formId, selectedPersona } = get()
				if (!organizationId || !formId) return

				set({ isLoading: true, error: null })
				try {
					// Fetch org-level config (formId = null)
					const orgConfig = await trpcClient.admin.userDashboard.getEvaluationHeaderConfig.query({
						organizationId,
						formId: null,
						persona: null,
						skipFallback: false,
					})

					// Apply org config as new form-specific config (generate new IDs, set formId)
					const fieldsWithIds = (orgConfig || []).map((field, idx) => ({
						...field,
						id: `copied-${idx}-${Date.now()}`,
						formId,
						persona: selectedPersona,
						visibleToRoles: null,
					}))

					set({ fields: fieldsWithIds.sort((a, b) => a.order - b.order) })
				} catch (error) {
					set({ error: error instanceof Error ? error.message : 'Failed to copy org defaults' })
					throw error
				} finally {
					set({ isLoading: false })
				}
			},

			// Reset the store
			reset: () => {
				set({
					fields: [],
					availableFields: null,
					organizationId: null,
					formId: null,
					selectedPersona: null,
					isLoading: false,
					isSaving: false,
					error: null,
				})
			},

			// Add a new field
			addField: (field) => {
				const { fields, formId, selectedPersona } = get()
				const newField: FieldConfig = {
					...field,
					id: `new-${Date.now()}`,
					order: fields.length,
					formId,
					persona: selectedPersona,
					visibleToRoles: null,
				}
				set({ fields: [...fields, newField] })
			},

			// Remove a field by ID
			removeField: (id) => {
				set({ fields: get().fields.filter((f) => f.id !== id) })
			},

			// Update a field's name
			updateFieldName: (id, name) => {
				set({ fields: get().fields.map((f) => (f.id === id ? { ...f, name } : f)) })
			},

			// Move field up in order
			moveFieldUp: (index) => {
				if (index === 0) return
				const fields = [...get().fields]
				;[fields[index - 1], fields[index]] = [fields[index], fields[index - 1]]
				set({ fields: fields.map((f, i) => ({ ...f, order: i })) })
			},

			// Move field down in order
			moveFieldDown: (index) => {
				const fields = [...get().fields]
				if (index >= fields.length - 1) return
				;[fields[index], fields[index + 1]] = [fields[index + 1], fields[index]]
				set({ fields: fields.map((f, i) => ({ ...f, order: i })) })
			},

			// Replace all fields
			setFields: (fields) => {
				set({ fields })
			},

			setSelectedPersona: async (persona) => {
				set({ selectedPersona: persona, isLoading: true, error: null })
				try {
					await get().fetchConfig()
				} catch (error) {
					set({ error: error instanceof Error ? error.message : 'Failed to fetch config' })
				} finally {
					set({ isLoading: false })
				}
			},
		}),
		{ name: 'evaluation-header-store' },
	),
)
